import { Link } from "react-router-dom";
import { Heart } from "lucide-react";
import { toast } from "sonner";
import { cad, imgUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const { user, wishlist, toggleWishlist } = useAuth();
  const soldOut = (product.stock || 0) <= 0;
  const inWishlist = wishlist.includes(product.id);
  const images = product.images || [];

  const onWishlist = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error("Please sign in to save favourites");
      return;
    }
    const added = await toggleWishlist(product.id);
    toast.success(added ? "Added to wishlist" : "Removed from wishlist");
  };

  const onAdd = (e) => {
    e.preventDefault();
    add(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group block fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="relative overflow-hidden bg-white border border-soft-border rounded-sm">
        <div className="relative aspect-[4/5] overflow-hidden bg-blush">
          {images[0] && (
            <img
              src={imgUrl(images[0])}
              alt={product.name}
              className="w-full h-full object-cover img-zoom"
              loading="lazy"
            />
          )}
          {images[1] && (
            <img
              src={imgUrl(images[1])}
              alt={product.name}
              className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              loading="lazy"
            />
          )}
          <button
            data-testid={`wishlist-btn-${product.id}`}
            onClick={onWishlist}
            className="absolute top-3 right-3 z-10 h-9 w-9 grid place-items-center rounded-full bg-white/90 backdrop-blur border border-soft-border hover:bg-white transition-colors"
            aria-label="Add to wishlist"
          >
            <Heart
              className={`h-4 w-4 ${inWishlist ? "fill-gold text-gold" : "text-charcoal"}`}
            />
          </button>
          <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5">
            {(product.badges || []).map((b) => (
              <span
                key={b}
                className="eyebrow bg-white/90 backdrop-blur px-2.5 py-1 rounded-full text-[0.6rem] border border-soft-border"
              >
                {b}
              </span>
            ))}
          </div>
          {soldOut && (
            <div className="absolute inset-0 bg-white/55 grid place-items-center">
              <span className="eyebrow bg-charcoal text-white px-4 py-2">Sold Out</span>
            </div>
          )}
          {!soldOut && (
            <button
              data-testid={`quick-add-${product.id}`}
              onClick={onAdd}
              className="absolute bottom-0 inset-x-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-charcoal text-white text-sm py-3 font-display tracking-wide"
            >
              Add to Cart
            </button>
          )}
        </div>
      </div>
      <div className="pt-4 space-y-1">
        <p className="eyebrow text-[0.6rem]">{product.category}</p>
        <h3 className="font-serif text-lg text-charcoal leading-snug">{product.name}</h3>
        <p className="text-sm text-charcoal/70">{cad(product.price)}</p>
      </div>
    </Link>
  );
};
